import React, { useCallback, useEffect, useState } from "react";
import styles from "./OverviewHeader.module.scss";
import { DeleteIcon, EditIcon, ClipboardX, PrintIcon } from "../../modules/default-svg";
import {
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  SplitButton,
  Switch,
} from "@fluentui/react-components";
import Button from "../controls/button";
import HoverCard from "../hoverCard/HoverCard";
import deletionRequestService from "../../store/services/deletionRequest.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import { getAccountCurrency } from "../../utils/systemSettings";
import { BudgetChart, ProjectProgress } from "../charts/DashboardCharts";
import Skeleton from "react-loading-skeleton";
import { Spinner } from "react-bootstrap";
import { Settings2 } from "lucide-react";
import TeamManagement from "../teamManagement/TeamManagement";
import { getDeletePermission, getEditPermission } from "../../helpers/utils";

const OverviewHeader = (props) => {
  const {
    entityType,
    headerTitle,
    headerFields,
    parentData,
    showDeleteModal,
    showEditModal,
    isShowAction,
    isPendingDelete = false,
    handleRefresh,
    budgetSummary,
    progressSummary,
    loading = false,
    handleStatusReport = null,
    getReportLoading = false,
    handleScopeTeam = null,
    onScopeTeamUpdated
  } = props;
  const [title, setTitle] = useState("");
  const [logo, setLogo] = useState("");
  const [isDeleteRequestLoading, setIsDeleteRequestLoading] = useState(false);
  const dispatch = useDispatch();
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [reportOptions, setReportOptions] = useState([
    { id: "IncludeBudgets", label: "Budget Summary", value: true, isVisible: true },
    { id: "IncludeBudgetsForecast", label: "Forecast & Revenue Summary", value: true, isVisible: true },
    { id: "IncludeResources", label: "Resources Summary", value: true, isVisible: true },
    { id: "IncludeGanttView", label: "Milestones Gantt", value: true, isVisible: true },
    { id: "IncludeRisks", label: "Risks & Issues", value: true, isVisible: true },
    { id: "IncludeMilestones", label: "Milestones", value: true, isVisible: true },
    { id: "IncludeDecisionsLog", label: "Decisions Log", value: true, isVisible: true },
    { id: "IncludeChangeRequests", label: "Change Requests", value: true, isVisible: true },
    { id: "IncludePurchaseOrders", label: "Purchase Orders", value: true, isVisible: true },
    { id: "IncludeInvoiceRegistry", label: "Invoice Registry", value: true, isVisible: true },
    { id: "IncludeCommunicationPlans", label: "Communication Plans", value: true, isVisible: true },
    { id: "IncludeProjectsProgressReports", label: "Projects Progress", value: true, isVisible: true },
    { id: "IncludeAppendix", label: "Appendix", value: true, isVisible: true },
  ]);

  const canEdit = () => {
    switch (entityType.toLowerCase()) {
      case "client":
        return getEditPermission("Client");
      case "portfolio":
        return getEditPermission("Portfolio");
      case "program":
        return getEditPermission("Program");
      case "scope":
        return getEditPermission("Scope");
      default:
        return false;
    }
  };

  const canDelete = () => {
    switch (entityType.toLowerCase()) {
      case "client":
        return getDeletePermission("Client", {isPendingDelete : isPendingDelete});
      case "portfolio":
        return getDeletePermission("Portfolio", {isPendingDelete : isPendingDelete});
      case "program":
        return getDeletePermission("Program", {isPendingDelete : isPendingDelete});
      case "scope":
        return getDeletePermission("Scope", {isPendingDelete : isPendingDelete});
      default:
        return false;
    }
  };

  const handleReportOptionToggle = useCallback((optionId, checked) => {
    setReportOptions((prev) =>
      prev.map((option) =>
        option.id === optionId
          ? { ...option, value: checked }
          : option
      )
    );
  }, []);
  useEffect(() => {
    setTitle(headerTitle);
    setLogo(headerTitle?.logoUrl || headerTitle?.client?.image);
  }, [headerTitle]);

  const StatusReportRequested = () => {
    const options = reportOptions.reduce((acc, option) => {
      acc[option.id] = option.value;
      return acc;
    }, {});
    handleStatusReport(options);
  }
  const handleCancelDeleteRequest = () => {
    setIsDeleteRequestLoading(true);
    deletionRequestService
      .cancelDeletionRequest({ RefId: parentData.id, RefType: parentData.type })
      .then(() => {
        setIsDeleteRequestLoading(false);
        handleRefresh();
      })
      .catch(() => {
        setIsDeleteRequestLoading(false);
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      });
  };

  return loading ? (
    <div className={styles.overviewHeaderWrapper}>
      <div
        className={`${styles.overviewHeadingWrap} d-flex justify-content-between align-items-start`}
      >
        <div className="d-flex align-items-start gap-1 flex-col">
          <Skeleton
            height={30}
            width={250}
            baseColor="#e0e0e0"
            highlightColor="#ECE7FD"
          />
          <Skeleton
            height={25}
            width={200}
            baseColor="#e0e0e0"
            highlightColor="#ECE7FD"
          />
          <Skeleton
            height={20}
            width={150}
            baseColor="#e0e0e0"
            highlightColor="#ECE7FD"
          />
        </div>
        <div className="d-flex align-items-center gap-3 flex-col">
          <Skeleton
            height={30}
            width={250}
            baseColor="#e0e0e0"
            highlightColor="#ECE7FD"
          />
          <Skeleton
            height={20}
            width={200}
            baseColor="#e0e0e0"
            highlightColor="#ECE7FD"
          />
        </div>
        <div className="d-flex align-items-end gap-4 flex-col">
          <Skeleton
            height={30}
            width={250}
            baseColor="#e0e0e0"
            highlightColor="#ECE7FD"
          />
          <Skeleton
            height={20}
            width={200}
            baseColor="#e0e0e0"
            highlightColor="#ECE7FD"
          />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <Skeleton
          height={25}
          width={250}
          baseColor="#e0e0e0"
          highlightColor="#ECE7FD"
        />
        <Skeleton
          height={25}
          width={250}
          baseColor="#e0e0e0"
          highlightColor="#ECE7FD"
        />
        <Skeleton
          height={25}
          width={250}
          baseColor="#e0e0e0"
          highlightColor="#ECE7FD"
        />
      </div>
    </div>
  ) : (
    <div className={styles.overviewHeaderWrapper}>
      <div
        className={`${styles.overviewHeadingWrap} d-flex justify-content-between align-items-center`}
      >
        <div className="d-flex align-items-center">
          {logo && (
            <div className="flex flex-col items-center justify-center max-w-32 h-14 overflow-hidden">
              <img
                src={logo}
                alt={title?.name ? `${title.name} logo` : ""}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
          <h4 className="ms-3 heading22">{title.name}</h4>
          {isPendingDelete && (
            <span className="pill purple m-2">Pending Delete</span>
          )}
        </div>

        <>
          {progressSummary &&
            <div className="flex justify-center" style={{ zIndex: 0 }}>
              <ProjectProgress progress={progressSummary} />
            </div>
          }

          {budgetSummary &&
            <div className="flex justify-center">
              <BudgetChart
                budgetSummary={budgetSummary}
                currencySymbol={getAccountCurrency(settings)}
                className={"overview-budget-chart"}
              />
            </div>
          }
        </>
        <div className="d-flex align-items-center mt-3">
          {handleStatusReport &&
            (
              <Menu positioning="below-end">
                <MenuTrigger disableButtonEnhancement>
                  {(triggerProps) => (
                    <SplitButton
                      menuButton={{
                        ...triggerProps,
                        menuIcon: <Settings2 color="#342c82" size={12} />,
                        className: `${styles.reportMenuButton}`,
                      }}
                      primaryActionButton={{
                        className: `${styles.reportPrimaryButton}`,
                        onClick: StatusReportRequested,
                        disabled: getReportLoading,
                      }}
                      disabled={getReportLoading}

                    >
                      <div className="flex col gap-2 align-items-center justify-content-end">
                        {getReportLoading ?
                          (<>
                            <Spinner
                              className="me-1"
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            Loading...
                          </>) :
                          (<>
                            <PrintIcon />
                            Status Report
                          </>)
                        }
                      </div>

                    </SplitButton>
                  )}
                </MenuTrigger>

                <MenuPopover className={styles.reportOptionsPopover}>
                  <MenuList className={styles.reportOptionsList}>
                    {reportOptions
                      .filter((option) => option.isVisible)
                      .map((option) => (
                        <MenuItem
                          key={option.id}
                          className={styles.reportOptionItem}
                          disabled={getReportLoading}
                          persistOnClick={true}
                        >
                          <div
                            className={styles.reportOptionContent}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReportOptionToggle(option.id, !option.value);
                            }}
                          >
                            <span>
                              {option.label}
                            </span>
                            <Switch
                              checked={option.value}
                              onChange={(ev, data) => {
                                ev.stopPropagation();
                                handleReportOptionToggle(option.id, data.checked);
                              }}
                              className={`${styles.reportOptionSwitch} ToggleStyle`}
                            />
                          </div>
                        </MenuItem>
                      ))}
                  </MenuList>
                </MenuPopover>
              </Menu>
            )
          }
          {handleScopeTeam && !loading &&
            (
              <TeamManagement
                entityType={"scope"}
                entityId={handleScopeTeam?.id}
                parentId={handleScopeTeam?.parentId}
                onTeamUpdate={onScopeTeamUpdated}
                initialTeamUsers={handleScopeTeam?.resources}
              />
            )
          }
          {isShowAction && (
            <>
              <div className={styles.overviewCustomBtn}>
                {isPendingDelete ? (
                  <Button
                    label="Cancel Delete Request"
                    icon={
                      <span className="icons20">
                        <ClipboardX />
                      </span>
                    }
                    clickHandler={handleCancelDeleteRequest}
                    showLoading={isDeleteRequestLoading}
                    btnClass={`${styles.noRightBorder}`}
                  />
                ) : (
                  canDelete() && (
                    <Button
                      label="Delete"
                      icon={
                        <span className="icons20">
                          <DeleteIcon color="#342c82" />
                        </span>
                      }
                      clickHandler={() => showDeleteModal()}
                      btnClass={`${canEdit() ? styles.noRightBorder : ""}`}
                    />
                  )
                )}
                {
                  canEdit() && (
                    <Button
                      label="Edit"
                      icon={
                        <span className="icons20">
                          <EditIcon color="#342c82" />
                        </span>
                      }
                      clickHandler={() => showEditModal()}
                      btnClass={`${canDelete() ? styles.noLeftBorderRadius : ""}`}
                    />
                  )
                }

                {/* <span className={styles.meatBallIcon}>
                              <MeatBallsMenuIcon />
                          </span> */}
              </div>
            </>

          )}
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        {headerFields &&
          headerFields.map((field) => (
            <div className={styles.overviewAvatarWrapper}>
              <p className="text-sm-400 BaseSecondColor mb-6">{field.label}</p>
              <div className="d-flex align-items-center">
                {field.hasOwnProperty("image") && field.name && (
                  <HoverCard
                    HoverCardTrigger={
                      <Avatar
                        size={28}
                        color="colorful"
                        name={field.name}
                        image={{ src: field.image || null }}
                      />
                    }
                    AvatarId={field.id}
                    AvatarName={field.name}
                    AvatarImage={field.image}
                    AvatarDesignation={field.title}
                    AvatarEmail={field.email}
                    AvatarPhone={field.phoneNumber}
                    AvatarIsExternal={field.isResource ? field.isExternal : true}
                    CanSendMessage={field.isResource ? true : false}
                  />
                )}
                <p className="text-md-500 ms-1 BaseMainColor">
                  {field.name || <>&nbsp;</>}
                </p>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default OverviewHeader;
